import { API, graphqlOperation } from 'aws-amplify';

import { createDriverQualificationServiceTeamNotes, runDqfAudit } from '../../../graphql/customMutations';
import { listActiveUserCustomersByCustomer, getUserStatus } from '../../../graphql/customQueries';
import { listDqfAuditsByDqfId, listDriverQualificationServiceTeamNotes } from '../../../graphql/queries';

// Returns true when the customer has at least one active client-contact whose user is still Active,
// meaning the DQF can be sent. Query errors are intentionally allowed to propagate so the caller can
// tell a genuine "no active users" result apart from an infrastructure failure (which must NOT be
// reported to the user as "no active users").
export const hasActiveUsers = async (id, nextToken = null) => {
  const response = await API.graphql({
    query: listActiveUserCustomersByCustomer,
    variables: { customerId: id, nextToken },
  });
  const page = response?.data?.listUserCustomers;
  const contacts = page?.items ?? [];

  for (const { userCustomersId } of contacts.filter(c => c?.userCustomersId)) {
    const userResponse = await API.graphql({
      query: getUserStatus,
      variables: { id: userCustomersId },
    });
    if (userResponse?.data?.getUser?.status === 'Active') {
      return true;
    }
  }

  if (page?.nextToken && page.nextToken !== nextToken) {
    return hasActiveUsers(id, page.nextToken);
  }
  return false;
};

// CP-836 slice 3 — kick off an async DQF AI audit (returns a PROCESSING DqfAudit). @function resolver.
export const startDqfAudit = async dqfId => {
  try {
    const response = await API.graphql({ query: runDqfAudit, variables: { dqfId } });
    return response.data?.runDqfAudit;
  } catch (e) {
    console.log('Error starting DQF audit', e);
    return { errors: e };
  }
};

// True if this DQF already has at least one AI evaluation on record (drives the button state machine).
export const hasDqfAudits = async dqfId => {
  try {
    const response = await API.graphql({ query: listDqfAuditsByDqfId, variables: { dqfId, limit: 1 } });
    return (response.data?.listDqfAuditsByDqfId?.items?.length || 0) > 0;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getSkillsFromDriver = async driverId => {
  const endpoint = `drivers/${driverId}`;
  try {
    const response = await API.get('centerlineportal', endpoint);
    if (response.response_code === 200) {
      return response.response_body;
    }
    return undefined;
  } catch (e) {
    if (e?.message?.includes('404')) {
      return null;
    }
    console.log(e);
    return undefined;
  }
};

export const fetchServiceTeamNotes = async dqfId => {
  try {
    const response = await API.graphql(
      graphqlOperation(listDriverQualificationServiceTeamNotes, {
        filter: { driverQualificationDriverQualificationServiceTeamNotesId: { eq: dqfId } },
      }),
    );
    return response.data.listDriverQualificationServiceTeamNotes;
  } catch (e) {
    console.log('Error fetching service team notes', e);
    return { items: [], nextToken: null };
  }
};

export const addServiceTeamNote = async (dqfId, user, text, createUserFullName) => {
  try {
    const input = {
      driverQualificationDriverQualificationServiceTeamNotesId: dqfId,
      user,
      text,
      createUserFullName,
    };
    const response = await API.graphql(graphqlOperation(createDriverQualificationServiceTeamNotes, { input }));
    return response.data.createDriverQualificationServiceTeamNotes;
  } catch (e) {
    console.log('Error creating service team note', e);
    return null;
  }
};
